import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

interface Step {
  number: string;
  title: string;
  description: string;
}

export function HowItWorks() {
  const { t, tArray } = useTranslation();
  const steps = tArray<Step>('howItWorks.steps');

  const icons = [
    (
      <svg viewBox="0 0 48 48" className="w-12 h-12" key="icon1">
        <path
          d="M8 12h32M8 24h32M8 36h32"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="16" cy="12" r="3" fill="#7ab8c9" opacity="0.5" />
        <circle cx="32" cy="24" r="3" fill="#7ab8c9" opacity="0.5" />
        <circle cx="24" cy="36" r="3" fill="#7ab8c9" opacity="0.5" />
      </svg>
    ),
    (
      <svg viewBox="0 0 48 48" className="w-12 h-12" key="icon2">
        <rect
          x="8"
          y="8"
          width="32"
          height="32"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M8 32L18 22L28 28L40 16"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
        />
        <circle cx="18" cy="22" r="2" fill="#7ab8c9" />
        <circle cx="28" cy="28" r="2" fill="#7ab8c9" />
        <circle cx="40" cy="16" r="2" fill="#7ab8c9" />
      </svg>
    ),
    (
      <svg viewBox="0 0 48 48" className="w-12 h-12" key="icon3">
        <rect
          x="4"
          y="4"
          width="16"
          height="16"
          fill="#7ab8c9"
          opacity="0.3"
        />
        <rect
          x="28"
          y="4"
          width="16"
          height="16"
          fill="#7ab8c9"
          opacity="0.5"
        />
        <rect
          x="4"
          y="28"
          width="16"
          height="16"
          fill="#7ab8c9"
          opacity="0.7"
        />
        <rect
          x="28"
          y="28"
          width="16"
          height="16"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    (
      <svg viewBox="0 0 48 48" className="w-12 h-12" key="icon4">
        <path
          d="M12 8v32M12 8l12 8-12 8"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M28 16h12M28 24h12M28 32h8"
          stroke="#7ab8c9"
          strokeWidth="2"
          fill="none"
          opacity="0.6"
        />
      </svg>
    ),
  ];

  return (
    <section className="bg-slate-100 dark:bg-[#0d1318] py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          className="mb-20 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('howItWorks.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight">
            {t('howItWorks.title')}
          </h2>
        </motion.div>

        <motion.div
          className="mb-12 lg:mb-20 flex justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="relative group overflow-hidden rounded-3xl border border-slate-200 dark:border-[#1a2530] bg-white/50 dark:bg-[#0a0f14]/50 p-2 backdrop-blur-sm shadow-2xl transition-all duration-500 hover:border-slate-300 dark:hover:border-[#3d5a6e]">
            <img
              src="/how-it-works.png"
              alt="How it works"
              className="w-full h-auto max-w-4xl rounded-2xl pointer-events-none"
            />
            <div className="absolute inset-0 bg-linear-to-t from-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        </motion.div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-24 left-[calc(12.5%-1px)] right-[calc(12.5%-1px)] h-px bg-linear-to-r from-slate-200 dark:from-[#1a2530] via-slate-400 dark:via-[#3d5a6e] to-slate-200 dark:to-[#1a2530]" />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="text-center">
                  {/* Icon container */}
                  <div className="relative inline-flex items-center justify-center w-20 h-20 mb-6 rounded-xl border border-slate-200 dark:border-[#1a2530] bg-white dark:bg-[#0a0f14] transition-all duration-300 hover:shadow-md hover:border-slate-300 dark:hover:border-[#3d5a6e]">
                    {icons[index]}
                    {/* Step number badge */}
                    <span className="absolute -top-3 -right-3 w-8 h-8 flex items-center justify-center rounded-lg bg-slate-100 dark:bg-[#0d1318] border border-slate-300 dark:border-[#3d5a6e] text-[#3d6a7a] dark:text-[#7ab8c9] text-xs font-medium">
                      {step.number}
                    </span>
                  </div>

                  <h3 className="font-display text-slate-900 dark:text-[#e8ebe9] text-lg md:text-xl mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 dark:text-[#8a9a9e] text-sm md:text-base leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
